import { DisciplinePage } from '../../../shared/components/DisciplinePage';
import { useI18n } from '../../../../i18n';
import { useAuth } from '../../../../context/AuthContext';
import { AdminDashboard } from './AdminDashboard';
import { AdminEmpresa } from './AdminEmpresa';
import { AdminUsuarios } from './AdminUsuarios';
import { AdminPlataforma } from './AdminPlataforma';
import { AdminModulos } from './AdminModulos';
import { AdminIATab } from './AdminIATab';
import { AdminNotificaciones } from './AdminNotificaciones';
import { AdminI18nTab } from './AdminI18nTab';
import { AdminSignTab } from './AdminSignTab';
import { AdminBackoffice } from './AdminBackoffice';
import { AdminWorkflowsTab } from './AdminWorkflowsTab';

// Tabs por rol — PRD-002 prompt 0566 (ADR-023) + ADR-024 (0614).
// SUPERADMIN_PLATFORM ve TODO incluido Backoffice + Plataforma (cambio de plan/modulos).
// TENANT_OWNER ve TODO operativo del tenant + Empresa (branding, dominio, facturacion).
// TENANT_ADMIN ve operativo del tenant pero NO Empresa (queda OWNER-only desde 0614).
// TENANT_AUDITOR ve solo lectura (Dashboard + Notificaciones modo read).
// MEMBER no llega aqui (RequireRole bloquea en App.tsx).
const ROLES_PLATFORM = ['SUPERADMIN_PLATFORM'] as const;
// 0614 ADR-024: Empresa (branding/dominio/facturacion) requiere OWNER.
// El TENANT_ADMIN gestiona usuarios/notif/firma/IA/i18n pero no toca branding.
const ROLES_OWNER_ONLY = ['SUPERADMIN_PLATFORM', 'TENANT_OWNER'] as const;
const ROLES_TENANT_FULL = ['SUPERADMIN_PLATFORM', 'TENANT_OWNER', 'TENANT_ADMIN'] as const;
const ROLES_TENANT_VIEW = [
  'SUPERADMIN_PLATFORM',
  'TENANT_OWNER',
  'TENANT_ADMIN',
  'TENANT_AUDITOR',
] as const;

type RolUsuario = (typeof ROLES_TENANT_VIEW)[number];

export function AdminPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const role = (user?.role ?? 'MEMBER') as RolUsuario;

  const allTabs = [
    {
      key: 'dashboard',
      label: t.admin.tabs.dashboard,
      component: <AdminDashboard />,
      visibleFor: ROLES_TENANT_VIEW,
    },
    {
      key: 'backoffice',
      label: t.admin.tabs.backoffice,
      component: <AdminBackoffice />,
      visibleFor: ROLES_PLATFORM,
    },
    {
      key: 'empresa',
      label: t.admin.tabs.empresa,
      component: <AdminEmpresa />,
      // 0614 ADR-024: Empresa (branding, dominio personalizado, facturacion)
      // queda OWNER-only. TENANT_ADMIN ya no la ve.
      visibleFor: ROLES_OWNER_ONLY,
    },
    {
      key: 'modulos',
      label: t.admin.tabs.modulos,
      component: <AdminModulos />,
      visibleFor: ROLES_TENANT_VIEW, // read-only para TENANT_AUDITOR (filtrado dentro del componente)
    },
    {
      key: 'usuarios',
      label: t.admin.tabs.usuarios,
      component: <AdminUsuarios />,
      visibleFor: ROLES_TENANT_FULL,
    },
    {
      key: 'plataforma',
      label: t.admin.tabs.plataforma,
      component: <AdminPlataforma />,
      visibleFor: ROLES_PLATFORM, // cambio de plan + dominio + maxUsuarios — SOLO platform staff
    },
    {
      key: 'ia',
      label: t.admin.tabs.ia,
      component: <AdminIATab />,
      visibleFor: ROLES_TENANT_FULL,
    },
    {
      key: 'notificaciones',
      label: t.admin.tabs.notificaciones,
      component: <AdminNotificaciones />,
      visibleFor: ROLES_TENANT_VIEW,
    },
    {
      key: 'sign',
      label: t.admin?.tabs?.sign ?? 'Sign',
      component: <AdminSignTab />,
      visibleFor: ROLES_TENANT_FULL,
    },
    {
      key: 'workflows',
      label: t.admin?.tabs?.workflows ?? 'Workflows',
      component: <AdminWorkflowsTab />,
      visibleFor: ROLES_TENANT_FULL,
    },
    {
      key: 'i18n',
      label: t.admin.tabs.i18n,
      component: <AdminI18nTab />,
      visibleFor: ROLES_TENANT_FULL,
    },
  ];

  const visibleTabs = allTabs
    .filter((tab) => (tab.visibleFor as readonly string[]).includes(role))
    .map(({ key, label, component }) => ({ key, label, component }));

  return <DisciplinePage accentColor="#0E243E" tabs={visibleTabs} />;
}
